import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { reportsAPI, brandsAPI, categoriesAPI, suppliersAPI } from '../../lib/api';
import { useAuth } from '../../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Button } from '../../components/ui/button';
import { Card as ShadCard } from '../../components/ui/card';
import {
    BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid,
} from 'recharts';
import { PackageSearch, Wallet, AlertTriangle, PackageX, Hourglass, ArrowLeft, ChevronLeft, ChevronRight, Search } from 'lucide-react';
import { ExportBar, KPICard, ReportLoader, ReportEmpty, ReportError, useCurrency, downloadCsv } from '../../components/reports/ReportKit';
import { printReportDocument } from '../../lib/printReport';

const BUCKETS = {
    expired: { label: 'expired', cls: 'bg-[#E76F51] text-white' },
    '30': { label: '30', cls: 'bg-[#E76F51]/30 text-[#E76F51]' },
    '60': { label: '60', cls: 'bg-[#E9C46A]/30 text-[#8a6d00]' },
    '90': { label: '90', cls: 'bg-[#E9C46A]/15 text-[#8a6d00]' },
    '180': { label: '180', cls: 'bg-[#2A9D8F]/15 text-[#2A9D8F]' },
    safe: { label: 'safe', cls: 'bg-[#2A9D8F]/15 text-[#2A9D8F]' },
};

const InventoryReport = () => {
    const { t, i18n } = useTranslation(['entities', 'common']);
    const { isAdmin } = useAuth();
    const showCost = isAdmin();
    const fmtMoney = useCurrency();
    const [filters, setFilters] = useState({
        warehouse: 'all', brand_id: 'all', category_id: 'all', supplier_id: 'all', stock_status: 'all',
    });
    const [search, setSearch] = useState('');
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [page, setPage] = useState(1);
    const pageSize = 50;

    const [brands, setBrands] = useState([]);
    const [categories, setCategories] = useState([]);
    const [suppliers, setSuppliers] = useState([]);

    useEffect(() => {
        (async () => {
            try {
                const [b, c, s] = await Promise.all([
                    brandsAPI.getAll().catch(() => ({ data: [] })),
                    categoriesAPI.getAll().catch(() => ({ data: [] })),
                    suppliersAPI.getAll().catch(() => ({ data: [] })),
                ]);
                setBrands(b.data || []);
                setCategories(c.data || []);
                setSuppliers(s.data || []);
        } catch (err) {
            // Non-fatal — filter dropdowns just render without labels.
            console.error('[InventoryReport] failed to load filter data:', err?.response?.status || err?.message);
        }
        })();
    }, []);

    const load = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const params = { page, page_size: pageSize };
            if (filters.brand_id !== 'all') params.brand_id = filters.brand_id;
            if (filters.category_id !== 'all') params.category_id = filters.category_id;
            if (filters.supplier_id !== 'all') params.supplier_id = filters.supplier_id;
            if (filters.warehouse !== 'all') params.warehouse = filters.warehouse;
            if (filters.stock_status !== 'all') params.stock_status = filters.stock_status;
            const { data: res } = await reportsAPI.inventoryAnalytics(params);
            setData(res);
        } catch (e) {
            setError(e?.response?.data?.detail || e.message);
            toast.error(t('common:toasts.load_failed'));
        } finally {
            setLoading(false);
        }
    }, [filters, page, t]);

    useEffect(() => { load(); }, [load]);

    const filteredRows = (data?.rows || []).filter((r) => {
        if (!search) return true;
        const q = search.toLowerCase();
        return (
            r.sku?.toLowerCase().includes(q) ||
            r.name?.toLowerCase().includes(q) ||
            r.brand?.toLowerCase().includes(q) ||
            r.category?.toLowerCase().includes(q)
        );
    });

    const exportCsv = () => {
        if (!filteredRows.length) return toast.error(t('entities:reports.toasts.nothing_to_export', { defaultValue: 'Nothing to export' }));
        const columns = [
            { key: 'sku', label: t('entities:products.columns.sku') },
            { key: 'name', label: t('entities:products.columns.name') },
            { key: 'brand', label: t('entities:batches.dialog.fields.product', { defaultValue: 'Brand' }) },
            { key: 'category', label: t('entities:products.columns.category') },
            { key: 'stock_qty', label: t('entities:products.columns.stock') },
            { key: 'unit', label: t('entities:products.dialog.unit') },
            ...(showCost ? [{ key: 'cost_price', label: t('entities:products.dialog.cost_price') }] : []),
            { key: 'price', label: t('entities:products.dialog.price') },
            ...(showCost ? [{ key: 'cost_value', label: t('entities:reports.inventory.cost_value', { defaultValue: 'Cost Value' }) }] : []),
            { key: 'retail_value', label: t('entities:reports.inventory.retail_value', { defaultValue: 'Retail Value' }) },
            { key: 'status', label: t('common:common.status') },
            { key: 'expiry_bucket', label: t('entities:expiry.columns.window') },
        ];
        downloadCsv(`inventory-report-${format(new Date(), 'yyyy-MM-dd')}.csv`, columns, filteredRows);
        toast.success(t('entities:reports.toasts.exported'));
    };

    const handlePrint = () => {
        if (!filteredRows.length) {
            toast.error(t('entities:reports.toasts.nothing_to_export', { defaultValue: 'Nothing to export' }));
            return;
        }
        const lang = i18n.language || 'fr';
        const moneyTxt = (v) => fmtMoney(v).replace(/\u00a0/g, ' ');
        const rows = filteredRows.map((r) => `
            <tr>
                <td style="font-family:'Courier New',monospace;">${r.sku || '—'}</td>
                <td>${r.name || '—'}</td>
                <td>${r.brand || '—'}</td>
                <td>${r.category || '—'}</td>
                <td class="num">${r.stock_qty ?? 0}</td>
                ${showCost ? `<td class="num">${moneyTxt(r.cost_price || 0)}</td>` : ''}
                <td class="num">${moneyTxt(r.price || 0)}</td>
                ${showCost ? `<td class="num">${moneyTxt(r.cost_value || 0)}</td>` : ''}
                <td class="num">${moneyTxt(r.retail_value || 0)}</td>
                <td style="text-transform:capitalize;">${r.status || '—'}</td>
            </tr>
        `).join('');
        const k = data?.kpis || {};
        const metaHtml = `
            <div class="kpi-strip">
                <div class="kpi"><span class="k">SKUs</span><span class="v">${k.total_skus ?? 0}</span></div>
                ${showCost ? `<div class="kpi accent"><span class="k">${lang.startsWith('fr') ? 'Valeur Coût' : 'Cost Value'}</span><span class="v">${moneyTxt(k.total_cost_value)}</span></div>` : ''}
                <div class="kpi accent"><span class="k">${lang.startsWith('fr') ? 'Valeur Vente' : 'Retail Value'}</span><span class="v">${moneyTxt(k.total_retail_value)}</span></div>
                <div class="kpi danger"><span class="k">${lang.startsWith('fr') ? 'Stock faible' : 'Low stock'}</span><span class="v">${k.low_stock_count ?? 0}</span></div>
                <div class="kpi danger"><span class="k">${lang.startsWith('fr') ? 'Rupture' : 'Out of stock'}</span><span class="v">${k.out_of_stock_count ?? 0}</span></div>
            </div>
        `;
        const contentHtml = `
            <table class="report-table">
                <thead><tr>
                    <th>${lang.startsWith('fr') ? 'Réf.' : 'SKU'}</th>
                    <th>${lang.startsWith('fr') ? 'Produit' : 'Product'}</th>
                    <th>${lang.startsWith('fr') ? 'Marque' : 'Brand'}</th>
                    <th>${lang.startsWith('fr') ? 'Catégorie' : 'Category'}</th>
                    <th class="num">${lang.startsWith('fr') ? 'Qté' : 'Qty'}</th>
                    ${showCost ? `<th class="num">${lang.startsWith('fr') ? 'P.A.' : 'Cost'}</th>` : ''}
                    <th class="num">${lang.startsWith('fr') ? 'P.V.' : 'Price'}</th>
                    ${showCost ? `<th class="num">${lang.startsWith('fr') ? 'V. Coût' : 'Cost V.'}</th>` : ''}
                    <th class="num">${lang.startsWith('fr') ? 'V. Vente' : 'Retail V.'}</th>
                    <th>${lang.startsWith('fr') ? 'Statut' : 'Status'}</th>
                </tr></thead>
                <tbody>${rows}</tbody>
            </table>
        `;
        printReportDocument({
            title: lang.startsWith('fr') ? 'Rapport d\'Inventaire' : 'Inventory Report',
            subtitle: format(new Date(), 'dd MMMM yyyy'),
            metaHtml,
            contentHtml,
            language: lang,
        });
    };

    const k = data?.kpis;
    const total = filteredRows.length;
    const totalPages = Math.max(1, Math.ceil(total / pageSize));
    const pageRows = filteredRows.slice((page - 1) * pageSize, page * pageSize);

    const statusBadge = (status) => {
        const map = {
            in: { label: t('entities:reports.inventory.status.in_stock', { defaultValue: 'In Stock' }), cls: 'bg-[#2A9D8F]/15 text-[#2A9D8F]' },
            low: { label: t('entities:reports.inventory.status.low', { defaultValue: 'Low' }), cls: 'bg-[#E9C46A]/30 text-[#8a6d00]' },
            out: { label: t('entities:reports.inventory.status.out', { defaultValue: 'Out' }), cls: 'bg-[#E76F51]/20 text-[#E76F51]' },
        };
        const m = map[status] || map.in;
        return <Badge className={`${m.cls} font-medium`}>{m.label}</Badge>;
    };

    return (
        <div data-testid="inventory-report-page" className="space-y-6 animate-fadeIn">
            <div className="flex items-center justify-between no-print">
                <div>
                    <Link to="/reports" className="text-xs text-gray-500 hover:text-[#C08478] flex items-center gap-1 mb-2">
                        <ArrowLeft size={12} /> {t('entities:reports.hub.title')}
                    </Link>
                    <h1 className="text-2xl font-bold text-[#1A1A1A] tracking-tight">{t('entities:reports.hub.cards.inventory.title')}</h1>
                    <p className="text-gray-500 text-sm mt-1">{t('entities:reports.hub.cards.inventory.description')}</p>
                </div>
                <ExportBar onCsv={exportCsv} onPrint={handlePrint} disabled={!filteredRows.length} />
            </div>

            {/* Filter bar */}
            <ShadCard className="border-0 shadow-sm sticky top-0 z-10 no-print">
                <CardContent className="p-3 flex flex-wrap items-center gap-3">
                    <div className="relative">
                        <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
                        <Input data-testid="inventory-search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder={t('entities:products.search_placeholder')} className="pl-8 h-9 w-56 bg-[#F5F5F5] text-sm" />
                    </div>
                    <Select value={filters.brand_id} onValueChange={(v) => { setFilters((f) => ({ ...f, brand_id: v })); setPage(1); }}>
                        <SelectTrigger className="h-9 w-36" data-testid="filter-brand"><SelectValue /></SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">{t('entities:products.filters.all_brands')}</SelectItem>
                            {brands.map((b) => (<SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>))}
                        </SelectContent>
                    </Select>
                    <Select value={filters.category_id} onValueChange={(v) => { setFilters((f) => ({ ...f, category_id: v })); setPage(1); }}>
                        <SelectTrigger className="h-9 w-40" data-testid="filter-category"><SelectValue /></SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">{t('entities:products.filters.all_categories')}</SelectItem>
                            {categories.map((c) => (<SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>))}
                        </SelectContent>
                    </Select>
                    <Select value={filters.supplier_id} onValueChange={(v) => { setFilters((f) => ({ ...f, supplier_id: v })); setPage(1); }}>
                        <SelectTrigger className="h-9 w-40" data-testid="filter-supplier"><SelectValue /></SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">{t('entities:batches.filters.all_suppliers')}</SelectItem>
                            {suppliers.map((s) => (<SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>))}
                        </SelectContent>
                    </Select>
                    <Select value={filters.stock_status} onValueChange={(v) => { setFilters((f) => ({ ...f, stock_status: v })); setPage(1); }}>
                        <SelectTrigger className="h-9 w-36" data-testid="filter-stock-status"><SelectValue /></SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">{t('common:common.all')}</SelectItem>
                            <SelectItem value="in">{t('entities:reports.inventory.status.in_stock', { defaultValue: 'In Stock' })}</SelectItem>
                            <SelectItem value="low">{t('entities:reports.inventory.status.low', { defaultValue: 'Low' })}</SelectItem>
                            <SelectItem value="out">{t('entities:reports.inventory.status.out', { defaultValue: 'Out' })}</SelectItem>
                        </SelectContent>
                    </Select>
                </CardContent>
            </ShadCard>

            {loading && <ReportLoader />}
            {error && !loading && <ReportError message={error} onRetry={load} />}

            {!loading && !error && k && (
                <>
                    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
                        <KPICard testId="kpi-skus" icon={PackageSearch} accent="info" label={t('entities:reports.inventory.kpi.total_skus')} value={k.total_skus} />
                        {showCost && <KPICard testId="kpi-stock-value" icon={Wallet} accent="success" label={t('entities:reports.inventory.kpi.stock_value_cost')} value={fmtMoney(k.stock_value_cost)} hint={`${t('entities:reports.inventory.kpi.retail', { defaultValue: 'Retail' })}: ${fmtMoney(k.stock_value_retail)}`} />}
                        {showCost && <KPICard testId="kpi-margin" icon={Wallet} accent="accent" label={t('entities:reports.inventory.kpi.potential_margin')} value={fmtMoney(k.potential_margin)} />}
                        <KPICard testId="kpi-low" icon={AlertTriangle} accent="warning" label={t('entities:reports.inventory.kpi.low_stock')} value={k.low_stock_count} />
                        <KPICard testId="kpi-out" icon={PackageX} accent="danger" label={t('entities:reports.inventory.kpi.out_of_stock')} value={k.out_of_stock_count} hint={`${k.expiring_count} ${t('entities:reports.inventory.kpi.expiring_soon', { defaultValue: 'expiring' })}`} />
                    </div>

                    {/* Top moving */}
                    <Card className="border-0 shadow-sm">
                        <CardHeader><CardTitle className="text-sm flex items-center gap-2"><Hourglass size={14} /> {t('entities:reports.inventory.top_moving_title', { defaultValue: 'Top moving (90 days)' })}</CardTitle></CardHeader>
                        <CardContent>
                            {data.charts.top_moving.length === 0 ? <ReportEmpty message={t('entities:reports.empty')} /> : (
                                <ResponsiveContainer width="100%" height={220}>
                                    <BarChart data={data.charts.top_moving} layout="vertical">
                                        <CartesianGrid strokeDasharray="3 3" stroke="#F0F0F0" />
                                        <XAxis type="number" tick={{ fontSize: 11 }} />
                                        <YAxis dataKey="name" type="category" width={140} tick={{ fontSize: 11 }} />
                                        <Tooltip formatter={(v, key) => key === 'revenue' ? fmtMoney(v) : v} />
                                        <Bar dataKey="qty" fill="#264653" radius={[0, 4, 4, 0]} />
                                    </BarChart>
                                </ResponsiveContainer>
                            )}
                        </CardContent>
                    </Card>

                    {/* Detail */}
                    <Card className="border-0 shadow-sm">
                        <CardHeader className="flex flex-row items-center justify-between">
                            <CardTitle className="text-sm">{t('entities:reports.inventory.detail_title', { defaultValue: 'Stock detail' })} ({total})</CardTitle>
                            <div className="flex items-center gap-2 no-print">
                                <Button size="icon" variant="ghost" className="h-7 w-7" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}><ChevronLeft size={14} /></Button>
                                <span className="text-xs text-gray-500">{page} / {totalPages}</span>
                                <Button size="icon" variant="ghost" className="h-7 w-7" disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)}><ChevronRight size={14} /></Button>
                            </div>
                        </CardHeader>
                        <CardContent>
                            {pageRows.length === 0 ? <ReportEmpty message={t('entities:reports.empty')} /> : (
                                <div className="overflow-x-auto">
                                    <Table>
                                        <TableHeader>
                                            <TableRow>
                                                <TableHead>{t('entities:products.columns.sku')}</TableHead>
                                                <TableHead>{t('entities:products.columns.name')}</TableHead>
                                                <TableHead>{t('entities:products.columns.category')}</TableHead>
                                                <TableHead className="text-right">{t('entities:products.columns.stock')}</TableHead>
                                                {showCost && <TableHead className="text-right">{t('entities:products.dialog.cost_price')}</TableHead>}
                                                {showCost && <TableHead className="text-right">{t('entities:reports.inventory.cost_value', { defaultValue: 'Cost Value' })}</TableHead>}
                                                <TableHead className="text-right">{t('entities:reports.inventory.retail_value', { defaultValue: 'Retail Value' })}</TableHead>
                                                <TableHead>{t('common:common.status')}</TableHead>
                                                <TableHead>{t('entities:expiry.columns.window')}</TableHead>
                                            </TableRow>
                                        </TableHeader>
                                        <TableBody>
                                            {pageRows.map((r) => {
                                                const b = BUCKETS[r.expiry_bucket] || BUCKETS.safe;
                                                return (
                                                    <TableRow key={r.product_id} data-testid={`inventory-row-${r.sku}`}>
                                                        <TableCell className="font-mono text-sm">{r.sku}</TableCell>
                                                        <TableCell>
                                                            <div className="font-medium">{r.name}</div>
                                                            {r.brand && <div className="text-[10px] text-gray-400">{r.brand}</div>}
                                                        </TableCell>
                                                        <TableCell className="text-sm">{r.category || '-'}</TableCell>
                                                        <TableCell className="text-right font-mono">{r.stock_qty} {r.unit}</TableCell>
                                                        {showCost && <TableCell className="text-right text-gray-500">{fmtMoney(r.cost_price)}</TableCell>}
                                                        {showCost && <TableCell className="text-right">{fmtMoney(r.cost_value)}</TableCell>}
                                                        <TableCell className="text-right font-semibold">{fmtMoney(r.retail_value)}</TableCell>
                                                        <TableCell>{statusBadge(r.status)}</TableCell>
                                                        <TableCell>
                                                            {r.batch_count > 0 ? (
                                                                <Badge className={`${b.cls} font-medium text-[10px]`}>{t(`entities:batches.buckets.${b.label}`)}</Badge>
                                                            ) : <span className="text-gray-300 text-xs">—</span>}
                                                        </TableCell>
                                                    </TableRow>
                                                );
                                            })}
                                        </TableBody>
                                    </Table>
                                </div>
                            )}
                        </CardContent>
                    </Card>
                </>
            )}
        </div>
    );
};

export default InventoryReport;
